import * as React from 'react';
import { connect } from 'react-redux';
import { withTranslation, WithTranslation } from "react-i18next";
import { app, dialog, DialogSize, UrlDialogInfo } from "@microsoft/teams-js";
import { selectMessage, getMessagesList, getDraftMessagesList } from '../../actions';
import { getBaseUrl } from '../../configVariables';
import Overflow from '../OverFlow/sentMessageOverflow';
import './messages.scss';
import { TFunction } from "i18next";
import { formatNumber } from '../../i18n';
import moment from 'moment';
import { Spinner, Table, TableHeader, TableRow, TableHeaderCell, TableBody, TableCell, Text, Tooltip } from '@fluentui/react-components';
import { Checkmark24Regular, ErrorCircle24Regular, Dismiss24Regular, Chat24Regular } from '@fluentui/react-icons';

export interface ITaskInfo {
  title?: string;
  height?: number;
  width?: number;
  url?: string;
  card?: string;
  fallbackUrl?: string;
  completionBotId?: string;
}

export interface IMessage {
  id: string;
  title: string;
  sentDate: string;
  recipients: string;
  acknowledgements?: string;
  reactions?: string;
  responses?: string;
  token?: object;
  scheduledDate?: string;
  succeeded?: number;
  failed?: number;
  unknown?: number;
}

export interface IMessageProps extends WithTranslation {
  messagesList: IMessage[];
  selectMessage?: any;
  getMessagesList?: any;
  getDraftMessagesList?: any;
  parentCallback?: any;
}

export interface IMessageState {
  message: IMessage[];
  loader: boolean;
}

class Messages extends React.Component<IMessageProps, IMessageState> {
  readonly localize: TFunction;
  private interval: any;
  private isOpenTaskModuleAllowed: boolean;
  constructor(props: IMessageProps) {
    super(props);
    this.localize = this.props.t;
    this.isOpenTaskModuleAllowed = true;
    this.state = {
      message: this.props.messagesList,
      loader: true,
    };
    this.escFunction = this.escFunction.bind(this);
  }

  public async componentDidMount() {
    await app.initialize();
    this.props.getMessagesList();
    document.addEventListener("keydown", this.escFunction, false);
    this.interval = setInterval(() => {
      this.props.getMessagesList();
    }, 60000);
  }

  public componentWillUnmount() {
    document.removeEventListener("keydown", this.escFunction, false);
    clearInterval(this.interval);
  }

  public componentWillReceiveProps(nextProps: any) {
    if (this.props !== nextProps) {
      this.setState({
        message: nextProps.messagesList,
        loader: false
      });

      if (nextProps.messagesList[0] !== undefined) {
        this.props.parentCallback(nextProps.messagesList[0].token);
      }
    }
  }

  public render(): JSX.Element {
    if (this.state.loader) {
      return (
        <Spinner />
      );
    } else if (this.state.message.length === 0) {
      return (<div className="results">{this.localize("EmptySentMessages")}</div>);
    }
    else {
      return (
        <div className="list">
          <Table arial-label="Default table">
            <TableHeader>
              <TableRow>
                <TableHeaderCell key="title"><Text weight='bold' truncate={true} wrap={false}>{this.localize("TitleText")}</Text></TableHeaderCell>
                <TableHeaderCell key="status"></TableHeaderCell>
                <TableHeaderCell key="recipients"><Text weight='bold' truncate={true} wrap={false}>{this.localize("Recipients")}</Text></TableHeaderCell>
                <TableHeaderCell key="sent"><Text weight='bold' truncate={true} wrap={false}>{this.localize("Sent")}</Text></TableHeaderCell>
                <TableHeaderCell key="actions"></TableHeaderCell>

              </TableRow>
            </TableHeader>
            <TableBody>
              {this.state.message.map((item) => (
                <TableRow key={item.id} onClick={(e) => this.onRowClick(item)}>
                  <TableCell>
                    <div className='title-container'>
                      <Chat24Regular />
                      <Text truncate={true} wrap={false} style={{display: "block"}}>{item.title}</Text>
                    </div>
                  </TableCell>
                  <TableCell>{this.renderSendingText(item)}</TableCell>

                  <TableCell>
                    <div className='recipients-container' style={{ display: "flex" }}>
                      <Tooltip content={this.props.t("TooltipSuccess")} relationship="label">
                        <div className='tooltip-content'>
                          <Checkmark24Regular className='succeeded' />
                          <Text>{formatNumber(item.succeeded)}</Text>
                        </div>
                      </Tooltip>
                      <Tooltip content={this.props.t("TooltipFailure")} relationship="label">
                        <div className='tooltip-content'>
                          <Dismiss24Regular className='failed' />
                          <Text align='center'>{formatNumber(item.failed)}</Text>
                        </div>
                      </Tooltip>
                      {
                        item.unknown &&
                        <Tooltip content="Unknown" relationship="label">
                          <div className='tooltip-content'>
                            <ErrorCircle24Regular className='unknown' />
                            <span className="semiBold">{formatNumber(item.unknown)}</span>
                          </div>
                        </Tooltip>
                      }
                    </div>
                  </TableCell>

                  <TableCell>
                    <Text truncate={true} wrap={false}>
                      {item.scheduledDate && !item.sentDate ? "To be sent at " + moment(new Date(item.scheduledDate)).format("MM/DD/YYYY hh:mm A") : item.sentDate}
                    </Text>
                  </TableCell>
                  <TableCell>
                    <Overflow message={item} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      );
    }
  }

  private onRowClick = (message: IMessage) => {
    let url = getBaseUrl() + "/viewstatus/" + message.id + "?locale={locale}";
    this.onOpenTaskModule(null, url, this.localize("ViewStatus"));

  }

  private renderSendingText = (message: any) => {
    var text = "";
    switch (message.status) {
      case "Queued":
        text = this.localize("Queued");
        break;
      case "SyncingRecipients":
        text = this.localize("SyncingRecipients");
        break;
      case "InstallingApp":
        text = this.localize("InstallingApp");
        break;
      case "Sending":
        let sentCount =
          (message.succeeded ? message.succeeded : 0) +
          (message.failed ? message.failed : 0) +
          (message.unknown ? message.unknown : 0);

        text = this.localize("SendingMessages", { "SentCount": formatNumber(sentCount), "TotalCount": formatNumber(message.totalMessageCount) });
        break;
      case "Sent":
      case "Failed":
        text = "";
    }

    return (<Text truncate={true} wrap={false} >{text}</Text>);
  }

  private escFunction = (event: any) => {
    if (event.keyCode === 27 || (event.key === "Escape")) {
      dialog.url.submit();
    }
  }

  public onOpenTaskModule = (event: any, url: string, title: string) => {
    if (this.isOpenTaskModuleAllowed) {
      this.isOpenTaskModuleAllowed = false;
      let taskInfo: UrlDialogInfo = {
        url: url,
        title: title,
        size: {
          height: 660,
          width: 1000
        } as DialogSize,
        fallbackUrl: url,
      }

      let submitHandler = (result: dialog.ISdkResponse) => {
        this.isOpenTaskModuleAllowed = true;
      };

      dialog.url.open(taskInfo, submitHandler);
    }
  }
}

const mapStateToProps = (state: any) => {
  return { messagesList: state.messagesList };
}

const messagesWithTranslation = withTranslation()(Messages);
export default connect(mapStateToProps, { selectMessage, getMessagesList, getDraftMessagesList })(messagesWithTranslation);